import translationEN from '../locales/en.json';
import translationES from '../locales/es.json';
import translationPT from '../locales/pt.json';
import { partnerQuestions, type PartnerResult } from './constants';

interface TranslationResult {
  title: string;
  subtitle: string;
  whatHappeningTitle: string;
  whatHappeningInfo: string;
  whyRepairsTitle: string;
  whyRepairsInfo: string;
  actions: Array<{
    title: string;
    text: string;
  }>;
  nextStep: string;
}

interface Translation {
  results: {
    wannaTransform: string;
    bookButton: string;
    callInfo: string;
    facilitators: {
      description: string;
    };
    actionsTitle: string;
    actionsTitlePreventive: string;
    title: string;
    repeatButton: string;
    wannaTransformShort: string;
  };
  emailTemplate: {
    greeting: string;
    intro: string;
    patternIdentified: string;
    whatHappening: string;
    recommendedActions: string;
    footerRights: string;
    summaryTitle: string;
    responseLabel: string;
    notAnswered: string;
    subject: string;
  };
  quiz: {
    questions: Record<string, string>;
    results: Record<string, TranslationResult>;
  };
  home: {
    title: string;
    subtitle: string;
    description1: string;
    description2: string;
    cta: string;
    startButton: string;
    info: string;
  };
  form: {
    errorMessage: string;
    submitButton: string;
  };
}

const translations: Record<string, Translation> = {
  es: translationES as unknown as Translation,
  pt: translationPT as unknown as Translation,
  en: translationEN as unknown as Translation
};

export const generateEmailHtml = (
  result: PartnerResult,
  userName: string,
  allAnswers?: string[],
  lng: string = 'en'
): string => {
  let language = 'en';
  if (lng.startsWith('pt')) {
    language = 'pt';
  } else if (lng.startsWith('es')) {
    language = 'es';
  }
  const t = translations[language];

  // To find the pattern key, we look into partnerResults from constants
  const patternKey =
    Object.keys(translationES.quiz.results).find(
      (key) =>
        (translationES.quiz.results as Record<string, TranslationResult>)[key]
          .title === result.title
    ) ?? 'A';

  const translatedResult = t.quiz.results[patternKey];

  const actionsHtml = translatedResult.actions
    .map(
      (action, index) => `
    <div style="margin-bottom: 20px; padding: 20px; border-radius: 12px; background-color: #f9fafb; border-left: 4px solid #7c3aed;">
      <div style="font-weight: 800; color: #7c3aed; margin-bottom: 8px; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">${
        index + 1
      }. ${action.title}</div>
      <div style="color: #374151; line-height: 1.6; font-size: 15px;">${
        action.text
      }</div>
    </div>
  `
    )
    .join('');

  let answersHtml = '';
  if (allAnswers && allAnswers.length > 0) {
    const questionsAndAnswers = partnerQuestions
      .map((q, index) => {
        const answerValue = allAnswers[index];
        const option = q.options.find((opt) => opt.value === answerValue);

        // Translate question and option
        const translatedQuestion = t.quiz.questions[`q${q.id}`];
        const translatedOptionText = option
          ? t.quiz.questions[`q${q.id}_opt${option.value}`]
          : t.emailTemplate.notAnswered;

        return `
        <div style="margin-bottom: 15px; border-bottom: 1px solid #f3f4f6; padding-bottom: 10px;">
          <p style="margin: 0 0 5px 0; font-size: 14px; font-weight: 600; color: #374151;">${
            index + 1
          }. ${translatedQuestion}</p>
          <p style="margin: 0; font-size: 14px; color: #6b7280; font-style: italic;">${
            t.emailTemplate.responseLabel
          } ${translatedOptionText}</p>
        </div>
      `;
      })
      .join('');

    answersHtml = `
      <div style="margin-top: 50px; padding: 30px; background-color: #ffffff; border: 1px dashed #e5e7eb; border-radius: 16px;">
        <h3 style="color: #111827; font-size: 18px; font-weight: 700; margin-bottom: 20px; text-align: center;">${t.emailTemplate.summaryTitle}</h3>
        ${questionsAndAnswers}
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html lang="${language}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${t.emailTemplate.subject}</title>
      <style>
        @media only screen and (max-width: 600px) {
          .mobile-stack {
            display: block !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            text-align: center !important;
            align-items: center !important;
          }
          .mobile-stack img {
            margin: 0 auto !important;
          }
          .mobile-stack .whatsapp-link {
            justify-content: center !important;
            margin: 0 auto !important;
          }
          .mobile-image-container {
            margin-bottom: 20px !important;
          }
          .mobile-padding {
            padding: 20px !important;
          }
        }
      </style>
    </head>
    <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f3f4f6; color: #1f2937;">
      <table border="0" cellpadding="0" cellspacing="0" width="100%" style="table-layout: fixed; background-color: #f3f4f6;">
        <tr>
          <td align="center" style="padding: 40px 10px;" class="mobile-padding">
            <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border-radius: 20px; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);">

              <!-- Content -->
              <tr>
                <td style="padding: 40px;" class="mobile-padding">
                  <p style="margin: 0 0 20px 0; font-size: 18px; color: #111827;">${
                    t.emailTemplate.greeting
                  } <strong>${userName}</strong>,</p>
                  <p style="margin: 0 0 35px 0; font-size: 16px; line-height: 1.6; color: #4b5563;">${
                    t.emailTemplate.intro
                  }</p>
                  
                  <!-- Result Card -->
                  <div style="margin-bottom: 40px; text-align: center; padding: 35px 25px; background-color: #ffffff; border: 2px solid #f3f4f6; border-radius: 20px;">
                    <span style="display: block; font-size: 13px; color: #7c3aed; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 15px;">${
                      t.emailTemplate.patternIdentified
                    }</span>
                    <h2 style="margin: 0; color: #111827; font-size: 32px; font-weight: 800; letter-spacing: -1px; line-height: 1.2;">${
                      translatedResult.title
                    }</h2>
                    <div style="width: 50px; height: 4px; background-color: #7c3aed; margin: 25px auto;"></div>
                    <p style="margin: 0; color: #4b5563; font-size: 18px; line-height: 1.5; font-weight: 500;">${
                      translatedResult.subtitle
                    }</p>
                  </div>

                  <h3 style="color: #111827; font-size: 22px; font-weight: 700; margin-bottom: 15px; letter-spacing: -0.5px;">${
                    t.emailTemplate.whatHappening
                  }</h3>
                  <p style="color: #374151; font-size: 16px; line-height: 1.8; margin-bottom: 40px; white-space: pre-line;">${
                    translatedResult.whatHappeningInfo
                  }</p>

                  <div style="padding: 30px; background-color: #f4f2f2ff; border-radius: 16px; margin-bottom: 45px;">
                    <h3 style="color: #374151; font-size: 19px; font-weight: 700; margin-bottom: 12px; letter-spacing: -0.5px;">${
                      translatedResult.whyRepairsTitle
                    }</h3>
                    <p style="color: #374151; font-size: 16px; line-height: 1.7; margin-bottom: 0; white-space: pre-line;">${
                      translatedResult.whyRepairsInfo
                    }</p>
                  </div>

                  <h3 style="color: #111827; font-size: 22px; font-weight: 700; margin-bottom: 25px; letter-spacing: -0.5px;">${
                    t.emailTemplate.recommendedActions
                  }</h3>
                  <div style="margin-bottom: 50px;">
                    ${actionsHtml}
                  </div>

                  <!-- CTA Section -->
                  <div style="border-top: 2px solid #f3f4f6; padding-top: 50px; text-align: center;">
                    <h3 style="color: #111827; font-size: 24px; font-weight: 800; margin-bottom: 15px; letter-spacing: -0.5px;">${
                      t.results.wannaTransform
                    }</h3>
                    <p style="color: #4b5563; font-size: 17px; line-height: 1.7; margin-bottom: 35px; max-width: 480px; margin-left: auto; margin-right: auto;">${
                      translatedResult.nextStep
                    }</p>
                    
                    <a href="${
                      result.calendlyLink
                    }" style="display: inline-block; background-color: #7c3aed; color: #ffffff; padding: 12px 28px; border-radius: 64px; text-decoration: none; font-size: 14px; font-weight: 500; text-transform: uppercase; transition: transform 0.2s ease;">
                      ${t.results.bookButton}
                    </a>
                    <p style="margin-top: 12px; color: #9ca3af; font-size: 14px; font-weight: 500;">${
                      t.results.callInfo
                    }</p>
                  </div>

                </td>
              </tr>

              <!-- Signature Footer -->
              <tr>
                <td style="padding: 0 40px 40px 40px;" class="mobile-padding">
                  <div style="background-color: #f9fafb; border-radius: 20px; padding: 30px;">
                    <table border="0" cellpadding="0" cellspacing="0" width="100%">
                      <tr>
                        <td width="90" valign="top" class="mobile-stack mobile-image-container">
                          <img src="https://themindessence.com/wp-content/uploads/2026/01/Anderson-Ana-retrato-1-1.jpg" alt="Anderson y Ana" width="80" height="80" style="border-radius: 50%; object-fit: cover; ">
                        </td>
                        <td valign="top" style="padding-left: 20px;" class="mobile-stack">
                          <p style="margin: 0; font-size: 15px; line-height: 1.6; color: #374151;">
                            <strong>Anderson Miranda</strong> & <strong>Ana Luz Gardim</strong>
                          </p>
                          <p style="margin: 5px 0 0 0; font-size: 14px; line-height: 1.5; color: #6b7280;">
                            ${t.results.facilitators.description}
                            <div style="margin-top: 8px; ">
                                <a href="https://wa.me/34650177631" class="whatsapp-link" style="display: inline-flex; text-decoration: none; font-size: 15px; line-height: 1.6; color: #374151; align-items: center;">
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/512px-WhatsApp.svg.png" alt="" width="24" height="24" style="vertical-align: middle; margin-right: 4px;">
                                    <strong>+34 650 177 631</strong>
                                </a>
                            </div>
                        </p>
                        </td>
                      </tr>
                    </table>
                  </div>

                  ${answersHtml}

                  <div style="margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px;">
                    &copy; ${new Date().getFullYear()} ANDERSON MIRANDA COACHING.<br />${
    t.emailTemplate.footerRights
  }
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};
